import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Star, MapPin, Video } from "lucide-react";
import axios from "axios";
import { LANDING } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Featured coaches strip on the landing page.
 *
 * Pulls the marketplace's rating-sorted list and shows the top few. Keeps
 * the whole section optional — if there are no live coaches yet (empty
 * database, network hiccup), we hide the section entirely so the homepage
 * never leaks an empty state to a first-time visitor.
 */
export default function FeaturedCoaches() {
  const [coaches, setCoaches] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/coaches?sort=rating`)
      .then((r) => {
        if (cancelled) return;
        const list = (r.data?.coaches || []).filter(
          (c) => c.payments_ready && (c.average_rating || 0) >= 0,
        );
        setCoaches(list.slice(0, 4));
      })
      .catch(() => setCoaches([]))
      .finally(() => !cancelled && setLoaded(true));
    return () => { cancelled = true; };
  }, []);

  // Nothing to feature yet? Bail — the homepage stays clean.
  if (loaded && coaches.length === 0) return null;

  return (
    <section
      id="featured"
      data-testid="featured-coaches"
      className="py-24 md:py-32 border-t border-slate-100"
    >
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="mb-12 md:mb-16 flex items-end justify-between gap-6 flex-wrap"
        >
          <div>
            <div className="eyebrow">§ Featured</div>
            <h2 className="mt-4 font-display font-medium text-[36px] sm:text-[44px] md:text-[52px] leading-[1.02] tracking-[-0.035em] text-slate-900 max-w-[20ch]">
              Top-rated coaches, ready to book.
            </h2>
          </div>
          <button
            type="button"
            onClick={() => navigate("/marketplace")}
            data-testid="featured-see-all"
            className="text-slate-700 hover:text-slate-900 text-[13.5px] font-semibold inline-flex items-center gap-1.5"
          >
            See all coaches <ArrowUpRight className="w-4 h-4" strokeWidth={2.4} />
          </button>
        </motion.div>

        {!loaded ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="animate-pulse aspect-[3/4] rounded-3xl bg-slate-100" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {coaches.map((c, i) => (
              <FeaturedCoachCard key={c.slug} coach={c} index={i} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function FeaturedCoachCard({ coach, index }) {
  const rating = coach.average_rating || 0;
  const reviews = coach.review_count || 0;
  const primarySpecialty = (coach.specialties || [])[0];
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, delay: index * 0.06, ease: [0.22, 1, 0.36, 1] }}
    >
      <Link
        to={`/coach/${coach.slug}`}
        data-testid={`featured-coach-${coach.slug}`}
        className="group block"
      >
        <div className="relative aspect-[3/4] rounded-3xl overflow-hidden bg-slate-100">
          {coach.photo ? (
            <img
              src={coach.photo}
              alt={coach.name}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.05]"
            />
          ) : (
            <div className="absolute inset-0 bg-slate-800 grid place-items-center text-white font-display text-6xl">
              {(coach.name || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/15 to-transparent" />
          {/* Rating pill */}
          {rating > 0 && (
            <div className="absolute top-4 right-4 h-8 px-2.5 rounded-full bg-white/95 text-slate-900 inline-flex items-center gap-1 text-[12px] font-semibold">
              <Star className="w-3 h-3 fill-current" strokeWidth={0} />
              {rating.toFixed(1)}
              <span className="text-slate-400 font-normal">·{reviews}</span>
            </div>
          )}
          {/* Bottom label */}
          <div className="absolute inset-x-0 bottom-0 p-5 text-white">
            <div className="text-[11.5px] uppercase tracking-[0.22em] font-semibold text-white/70">
              {primarySpecialty || "Coach"}
            </div>
            <div className="mt-1.5 font-display text-[22px] leading-tight tracking-tight">{coach.name}</div>
            {coach.headline && (
              <div className="mt-1 text-[12.5px] text-white/80 line-clamp-2 leading-snug">
                {coach.headline}
              </div>
            )}
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between text-[13px] text-slate-600">
          <div className="inline-flex items-center gap-2 min-w-0">
            {coach.online_available ? (
              <Video className="w-3.5 h-3.5 text-slate-400 shrink-0" strokeWidth={2.4} />
            ) : (
              <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0" strokeWidth={2.4} />
            )}
            <span className="truncate">{coach.city || (coach.online_available ? "Online" : "In person")}</span>
          </div>
          <div className="font-semibold text-slate-900">
            ${coach.price_per_session}
            <span className="text-slate-400 font-normal text-[11.5px]"> / session</span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
